// src/utility/bridge/verifyEnvelope.js

/**
 * Verify a signed HMAC envelope.
 *
 * @param {object} envelope - Parsed envelope JSON.
 * @param {string} secret - HMAC secret.
 * @param {string} expectedKeyId - Expected keyId (kid).
 * @param {object} [options]
 * @param {number} [options.maxSkewMs=60000] - Allowed timestamp skew.
 * @returns {Promise<boolean>}
 */
export async function verifyEnvelope(
    envelope,
    secret,
    expectedKeyId,
    options = {}
) {
    try {
        if (!envelope || typeof envelope !== "object") return false;

        const {
            v,
            id,
            type,
            payload,
            ts,
            nonce,
            kid,
            sig,
        } = envelope;

        // Basic schema checks
        if (!v || !id || !type || payload === undefined || !ts || !nonce || !kid || !sig) {
            console.warn("[Bridge] Envelope schema invalid");
            return false;
        }

        // Key id check
        if (kid !== expectedKeyId) {
            console.warn("[Bridge] Envelope kid mismatch");
            return false;
        }

        let maxSkewMs = options.maxSkewMs ?? 60_000; // default 60s

        if (type === "INAPPBROWSER_CLOSED") {
            // Allow up to 5 minutes skew for this special case
            maxSkewMs = 300_000; // 5 minutes
        }

        console.log('Math.abs(Date.now() - ts)', Math.abs(Date.now() - ts))

        // Timestamp skew check
        if (Math.abs(Date.now() - ts) > maxSkewMs) {
            console.warn(`[Bridge] Envelope timestamp skew too large for type=${type}`);
            return false;
        }

        const signed = { v, id, type, payload, ts, nonce, kid };
        const toSign = canonicalStringify(signed);
        const expectedSig = await hmacBase64Url(toSign, secret);

        if (expectedSig !== sig) {
            console.warn("[Bridge] Envelope signature mismatch");
            return false;
        }

        return true;
    } catch (err) {
        console.warn("[Bridge] verifyEnvelope failed:", err);
        return false;
    }
}

/**
 * Deterministic JSON (sorted keys) to avoid signature drift
 */
function canonicalStringify(obj) {
    return JSON.stringify(
        Object.keys(obj)
            .sort()
            .reduce((acc, key) => {
                acc[key] = obj[key];
                return acc;
            }, {})
    );
}

/**
 * HMAC-SHA256(data, secret) → base64url
 */
async function hmacBase64Url(data, secret) {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        "raw",
        enc.encode(secret),
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
    );

    const sig = await crypto.subtle.sign("HMAC", key, enc.encode(data));
    const b64 = btoa(String.fromCharCode(...new Uint8Array(sig)));

    // base64 → base64url
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
